import type { ReactNode } from 'react';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { SectionCard } from '@/components/common/SectionCard';
import { ChipGroup } from '@/components/ui/ChipGroup';
import { TextField } from '@/components/forms/TextField';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { formatCurrency, optionLabel } from '@/lib/utils/format.utils';
import {
  AI_CLASSIFICATION_OPTIONS,
  FIELD_LABELS,
  PAYING_CUSTOMERS_OPTIONS,
  REVENUE_GROWTH_RATE_OPTIONS,
  REVENUE_MODEL_OPTIONS,
  REVENUE_RANGE_OPTIONS,
} from '@/features/startups/constants';
import { StartupMetricFields } from '@/features/startups/components/StartupBusinessFields';
import type { StartupProfileData } from '../../api/startup-profile.api';
import type { StartupProfileFormValues } from './schema';
import { STARTUP_PROFILE_STRINGS as T } from './startup-profile.strings';

const S = UI_STRINGS.STARTUP_PROFILE;

function Fact({ label, value, hint }: { label: string; value?: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt>
        <FieldLabel label={label} variant="caps" />
      </dt>
      <dd className="text-navy text-p6">
        {value ?? <span className="text-slate">{S.NOT_PROVIDED}</span>}
      </dd>
      {hint && <p className="text-slate text-p6 mt-0.5">{hint}</p>}
    </div>
  );
}

interface SectionProps {
  /** View mode only: extra header content, e.g. the create wizard review's Edit link. */
  headerRight?: ReactNode;
  mode: 'view' | 'edit';
  data: StartupProfileData;
  control?: Control<StartupProfileFormValues>;
  register?: UseFormRegister<StartupProfileFormValues>;
  errors?: FieldErrors<StartupProfileFormValues>;
}

/**
 * Financials card: total revenue, ARR, amount raised, non-dilutive funding,
 * plus the business metrics the create wizard groups with them (r228). Use of
 * Funds and Investor Types Sought live in `CurrentRaiseSection` (FR-172).
 */
export function FinancialsSection({
  mode,
  data,
  control,
  register,
  errors,
  headerRight,
}: SectionProps) {
  if (mode === 'view' || !control || !register) {
    const revenueLabel = REVENUE_RANGE_OPTIONS.find(
      (o) => o.value === data.totalRevenueRange,
    )?.label;
    return (
      <SectionCard title={S.FINANCIALS} headerRight={headerRight}>
        <dl className="flex flex-col gap-4">
          <Fact label={S.TOTAL_REVENUE} value={revenueLabel} />
          <Fact label={S.ARR} value={formatCurrency(data.arr)} />
          <Fact label={S.AMOUNT_RAISED_TO_DATE} value={formatCurrency(data.amountRaisedToDate)} />
          <Fact
            label={S.NONDILUTIVE_FUNDING}
            value={formatCurrency(data.nondilutiveFunding)}
            hint={data.nondilutiveFunding != null ? S.NONDILUTIVE_FUNDING_HINT : undefined}
          />
          <Fact
            label={FIELD_LABELS.aiClassification}
            value={optionLabel(AI_CLASSIFICATION_OPTIONS, data.aiClassification)}
          />
          <Fact
            label={FIELD_LABELS.revenueModel}
            value={optionLabel(REVENUE_MODEL_OPTIONS, data.revenueModel)}
          />
          <Fact
            label={FIELD_LABELS.revenueGrowthRate}
            value={optionLabel(REVENUE_GROWTH_RATE_OPTIONS, data.revenueGrowthRate)}
          />
          <Fact
            label={FIELD_LABELS.payingCustomers}
            value={optionLabel(PAYING_CUSTOMERS_OPTIONS, data.payingCustomers)}
          />
        </dl>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={S.FINANCIALS}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <FieldLabel label={S.TOTAL_REVENUE} variant="caps" />
          <Controller
            control={control}
            name="totalRevenueRange"
            render={({ field }) => (
              <ChipGroup
                label={S.TOTAL_REVENUE}
                options={REVENUE_RANGE_OPTIONS}
                columns={4}
                value={field.value}
                onChange={field.onChange}
                error={errors?.totalRevenueRange?.message}
              />
            )}
          />
        </div>
        {/* Three dollar amounts on one row, with the card's short labels so each fits on one line. */}
        <div className="grid gap-5 sm:grid-cols-3">
          <Controller
            control={control}
            name="arr"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={S.ARR}
                labelVariant="caps"
                prefix="$"
                error={errors?.arr?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="amountRaisedToDate"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={T.RAISED_TO_DATE}
                labelVariant="caps"
                prefix="$"
                error={errors?.amountRaisedToDate?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="nondilutiveFunding"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={T.NONDILUTIVE_FUNDING}
                labelVariant="caps"
                prefix="$"
                error={errors?.nondilutiveFunding?.message}
              />
            )}
          />
        </div>
        <StartupMetricFields register={register} errors={errors} />
      </div>
    </SectionCard>
  );
}
